// React
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

// @mui material components
import {
  Card,
  Fade,
  Grid,
  Icon,
  ImageList,
  ImageListItem,
  Input,
  Modal,
  Tooltip,
} from "@mui/material";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SinDatos from "components/Carteles/SinDatos";
import SoftBox from "components/CheckBox";

const allowedFileTypes = ".jpg, .jpeg, .png, .pdf";

export default function ModalFotos({ open, handleClose, proveedor, esDNI = false, handleSubmit }) {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);

  const handleFiles = (e) => {
    const filesArray = Array.from(e.target.files);
    //check type is allowed and size is less than 5MB, if not set a error
    for (let i = 0; i < filesArray.length; i++) {
      if (!allowedFileTypes.includes(filesArray[i].type.split("/")[1])) {
        setError("Formatos permitidos: .jpg, .jpeg, .png, .pdf");
        return;
      }
      if (filesArray[i].size > 5 * 1024 * 1024) {
        setError("Tamaño de archivo excede el límite de 5MB");
        return;
      }
    }
    setError(null);
    setFiles((prev) => [...prev, ...filesArray]);
  };

  const handleSave = async () => {
    if (files) {
      if (esDNI) {
        handleSubmit(files);
      } else {
        handleSubmit(files);
      }
    }
    handleClose();
  };

  useEffect(() => {
    if (open && proveedor) {
      if (esDNI) {
        setFiles(proveedor?.documentosFile || []);
      } else {
        setFiles(proveedor?.certificadoFile || []);
      }
    } else {
      setFiles([]);
    }
  }, [open, proveedor]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            minHeight: "50%",
            width: esDNI ? { xs: "80%", sm: "70%", xxl: "40%" } : { xs: "80%", sm: "70%" },
            overflowY: "auto",
            p: 3,
          }}
        >
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <SoftTypography variant="h6" align="center">
                {esDNI ? "Cargar frente y dorso DNI" : "Cargar certificado"}
              </SoftTypography>

              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </Grid>
            <Grid item xs={12}>
              <SoftButton
                color="primary"
                fullWidth
                circular
                onClick={() => {
                  document.getElementById("input-file").click();
                }}
              >
                Agregar <Icon>upload</Icon>
              </SoftButton>
              {error && (
                <SoftBox display="flex" justifyContent="center" pt={1}>
                  <SoftTypography variant="caption" color="error" align="center">
                    {error}
                  </SoftTypography>
                </SoftBox>
              )}
            </Grid>
            <Grid item xs={12}>
              <ImageList sx={{ height: 220 }} cols={3} rowHeight={164}>
                {files.length ? (
                  <>
                    {files?.map((file, index) => (
                      <ImageListItem
                        key={index}
                        sx={{
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        {file.type?.startsWith("image") ? (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={proveedor?.nombre + "foto-" + index}
                            loading="lazy"
                            style={{ objectFit: "contain", width: "100%", height: "100%" }}
                          />
                        ) : (
                          <SoftBox
                            width="100%"
                            height="100%"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            flexDirection="column"
                            gap={1}
                          >
                            <SoftBox
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              width="100px"
                              height="100px"
                              border="1px solid #ccc"
                              borderRadius="1rem"
                            >
                              <Icon fontSize="large">insert_drive_file</Icon>
                            </SoftBox>
                            <SoftTypography variant="caption">{file.name}</SoftTypography>
                          </SoftBox>
                        )}
                        <Tooltip title="Eliminar" placement="top">
                          <Icon
                            sx={{
                              position: "absolute",
                              top: 5,
                              right: 5,
                              cursor: "pointer",
                              color: "white",
                              backgroundColor: "rgba(255,255,255,0.5)",
                              borderRadius: "50%",
                            }}
                            onClick={() => {
                              const newImagenes = files.filter((imagen, i) => i !== index);
                              setFiles(newImagenes);
                            }}
                          >
                            cancel
                          </Icon>
                        </Tooltip>
                      </ImageListItem>
                    ))}
                  </>
                ) : (
                  <>
                    <Grid
                      item
                      xs={12}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    ></Grid>
                    <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
                      <SinDatos
                        titulo="No hay documentos cargados"
                        mensaje="Los documentos cargados se mostrarán aquí"
                        icono="upload_file"
                      />
                    </Grid>
                  </>
                )}
              </ImageList>
            </Grid>
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent="flex-end"
              sx={{
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Grid
                mr={{
                  xs: 0,
                  sm: 2,
                }}
                mt={{
                  xs: 2,
                  sm: 0,
                }}
              >
                <SoftButton
                  color="primary"
                  circular
                  onClick={handleClose}
                  fullWidth={{
                    xs: true,
                    sm: false,
                  }}
                >
                  <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                  &nbsp;Cancelar
                </SoftButton>
              </Grid>
              <Grid
                mt={{
                  xs: 2,
                  sm: 0,
                }}
              >
                <SoftButton
                  color="uproGreen"
                  circular
                  fullWidth={{
                    xs: true,
                    sm: false,
                  }}
                  onClick={handleSave}
                >
                  <Icon sx={{ fontWeight: "light" }}>save</Icon>
                  &nbsp;Guardar
                </SoftButton>
              </Grid>
            </Grid>
            <Grid sx={{ display: "none" }}>
              <Input
                type="file"
                id="input-file"
                onChange={handleFiles}
                name="fotos"
                inputProps={{
                  accept: allowedFileTypes,
                  ...(esDNI ? { multiple: true } : { multiple: false }),
                }}
              />
            </Grid>
          </Grid>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalFotos.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  proveedor: PropTypes.object,
  esDNI: PropTypes.bool,
  handleSubmit: PropTypes.func,
};
